import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { Textarea } from '@/components/ui/Input';
import { NativeSelect } from '@/components/ui/Input';
import { toast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import {
  listMemory, createPreference, updatePreference, deletePreference, resetMemory, setMemoryPaused,
  type DoctorPreference, type MemoryCategory,
} from './memoryApi';

const CATEGORY_LABELS: Record<MemoryCategory, string> = {
  format: 'Format', wording: 'Wording', content: 'Content', language: 'Language',
};

/**
 * "My AI preferences" (§4.4 point 3) — review and manage what Doctor AI
 * Memory has learned, plus a place to add a preference directly rather than
 * waiting for a chat instruction. Reached from the profile menu (App.tsx)
 * and from the ✨ Personalised chip on any draft (PersonalisedChip.tsx).
 *
 * Phase 2 shows only 'explicit' and 'chat' rows, both always 'active' or
 * 'archived' — a 'learned'/'proposed' card (Phase 3's evidence-based mining)
 * has no UI here yet; listMemory() already returns those rows too (nothing
 * hides them), they just render with the same toggle/edit/delete controls
 * since there is nothing Phase-3-specific to show yet.
 */
export function MyAiPreferencesPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [status, setStatus] = useState<Awaited<ReturnType<typeof listMemory>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draftStatement, setDraftStatement] = useState('');
  const [draftCategory, setDraftCategory] = useState<MemoryCategory>('content');
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setStatus(await listMemory());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your AI preferences.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!draftStatement.trim()) return;
    setSaving(true);
    try {
      await createPreference({ category: draftCategory, statement: draftStatement.trim() });
      setDraftStatement('');
      await load();
      toast({ variant: 'success', title: 'Preference added', description: 'It applies from your next draft.' });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not add this preference', description: e instanceof ApiError ? e.message : undefined });
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p: DoctorPreference) => {
    try {
      await updatePreference(p.id, { active: p.status !== 'active' });
      await load();
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not update this preference', description: e instanceof ApiError ? e.message : undefined });
    }
  };

  const remove = async (p: DoctorPreference) => {
    try {
      await deletePreference(p.id);
      await load();
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not delete this preference', description: e instanceof ApiError ? e.message : undefined });
    }
  };

  const pause = async (paused: boolean) => {
    try {
      await setMemoryPaused(paused);
      await load();
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not update pause state', description: e instanceof ApiError ? e.message : undefined });
    }
  };

  // Confirmed in a dialog first — window.confirm() is refused by embedded browsers.
  const doReset = async () => {
    try {
      const { removed } = await resetMemory();
      await load();
      toast({ variant: 'success', title: `Removed ${removed} preference${removed === 1 ? '' : 's'}` });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not reset your AI memory', description: e instanceof ApiError ? e.message : undefined });
    }
  };

  const rows = status?.preferences ?? [];
  const active = rows.filter((p) => p.status === 'active');
  const other = rows.filter((p) => p.status !== 'active');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>🧠 My AI preferences</DialogTitle>
        </DialogHeader>

        <div className="text-xs text-muted-foreground">
          The assistant learns from your edits. Nothing is applied without your approval — every preference below is one you set directly, or explicitly asked to remember.
          {status && !status.enabled && ' Doctor AI Memory is currently off for this hospital, so none of these are applied right now.'}
        </div>

        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {error && <div className="text-sm text-danger">{error}</div>}

        {status && (
          <>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Pause personalisation</div>
                <div className="text-xs text-muted-foreground">Stop applying your preferences without deleting them.</div>
              </div>
              <Switch checked={status.paused} onCheckedChange={(v) => pause(v)} aria-label="Pause personalisation" />
            </div>

            <div className="flex flex-col gap-2">
              {active.length === 0 && <div className="text-sm text-muted-foreground">No active preferences yet.</div>}
              {active.map((p) => (
                <PreferenceRow key={p.id} pref={p} onToggle={() => toggle(p)} onDelete={() => remove(p)} />
              ))}
              {other.length > 0 && (
                <>
                  <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Off</div>
                  {other.map((p) => (
                    <PreferenceRow key={p.id} pref={p} onToggle={() => toggle(p)} onDelete={() => remove(p)} />
                  ))}
                </>
              )}
            </div>

            <div className="flex flex-col gap-2 rounded-md border border-border p-3">
              <div className="text-sm font-medium">+ Add a preference</div>
              <div className="flex gap-2">
                <NativeSelect value={draftCategory} onChange={(e) => setDraftCategory(e.target.value as MemoryCategory)} className="w-32 shrink-0">
                  {Object.entries(CATEGORY_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </NativeSelect>
                <Textarea
                  value={draftStatement}
                  onChange={(e) => setDraftStatement(e.target.value)}
                  placeholder="e.g. Omit normal systemic examination findings"
                  rows={2}
                  className="flex-1"
                />
              </div>
              <Button type="button" size="sm" onClick={add} disabled={saving || !draftStatement.trim()} className="self-end">
                {saving ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmReset(true)} disabled={!rows.length} className="sm:mr-auto">
            Reset my AI memory
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
        <ConfirmDialog
          open={confirmReset}
          onOpenChange={setConfirmReset}
          title="Remove every saved AI preference?"
          description="All your saved preferences are deleted. This only affects future drafts — nothing already approved changes."
          confirmLabel="Reset my AI memory"
          onConfirm={() => { void doReset(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function PreferenceRow({ pref, onToggle, onDelete }: { pref: DoctorPreference; onToggle: () => void; onDelete: () => void }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-md border border-border p-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-sm">{pref.statement}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          <Badge variant="neutral">{CATEGORY_LABELS[pref.category as MemoryCategory] ?? pref.category}</Badge>
          <span>{pref.doc_type ?? 'All document types'}</span>
          <span>· {pref.origin === 'chat' ? 'from a chat instruction' : pref.origin === 'learned' ? 'learned from your edits' : 'you set this'}</span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Switch checked={pref.status === 'active'} onCheckedChange={onToggle} aria-label={`Toggle: ${pref.statement}`} />
        <Button type="button" variant="ghost" size="sm" onClick={onDelete} aria-label={`Delete: ${pref.statement}`}>Delete</Button>
      </div>
    </div>
  );
}
